// AI Tutor streaming presentation (doc 27, ST-01) -- a partial reply that
// renders without flicker.
//
// The visible text is a prefix of the reply cut between two reveal units
// (revealUnits.js), so it never ends inside code, math, a link, a table or a
// fenced block. The one construct a word-level cut does open is emphasis:
// «این **نکتهٔ » would show two literal asterisks until the closing pair is
// revealed. `stabilizePartial` closes what is open -- on the VISIBLE COPY
// only. The authoritative text is never touched, and a complete text is
// returned exactly as it is, so the final render is the renderer's own.
//
// No Markdown is parsed or produced here; the result still goes through the
// one renderer (`renderMarkdown`).

const DELIMITERS = ['**', '__', '~~', '*', '_']

function isSpace(ch) {
  return ch === undefined || /\s/.test(ch)
}

function isWordChar(ch) {
  return ch !== undefined && /[0-9A-Za-zÀ-￿]/.test(ch) && ch !== '_'
}

// Skips what is rendered verbatim, so a `*` inside code or math is not read
// as emphasis. Returns the offset after the span, or `at` when none starts.
function skipVerbatim(text, at) {
  const ch = text[at]
  if (ch === '\\') return Math.min(text.length, at + 2)
  if (ch === '`') {
    let run = 1
    while (text[at + run] === '`') run += 1
    const close = text.indexOf(text.slice(at, at + run), at + run)
    return close === -1 ? text.length : close + run
  }
  if (ch === '$') {
    const double = text[at + 1] === '$'
    const close = text.indexOf(double ? '$$' : '$', at + (double ? 2 : 1))
    if (close === -1) return double ? text.length : at + 1
    return close + (double ? 2 : 1)
  }
  return at
}

function delimiterAt(text, at) {
  for (const delimiter of DELIMITERS) {
    if (text.startsWith(delimiter, at)) return delimiter
  }
  return ''
}

// The emphasis still open at the end of `text`, outermost first.
function openEmphasis(text) {
  const stack = []
  let at = 0
  while (at < text.length) {
    const skipped = skipVerbatim(text, at)
    if (skipped !== at) {
      at = skipped
      continue
    }
    const delimiter = delimiterAt(text, at)
    if (!delimiter) {
      at += 1
      continue
    }
    const before = text[at - 1]
    const after = text[at + delimiter.length]
    const underscore = delimiter[0] === '_'
    const top = stack[stack.length - 1]
    const canClose =
      top === delimiter &&
      !isSpace(before) &&
      (!underscore || !isWordChar(after))
    const canOpen = !isSpace(after) && (!underscore || !isWordChar(before))
    if (canClose) stack.pop()
    else if (canOpen) stack.push(delimiter)
    at += delimiter.length
  }
  return stack
}

function closesLater(rest, delimiter) {
  const paragraph = rest.split(/\n[ \t]*\n/)[0]
  let at = paragraph.indexOf(delimiter)
  while (at !== -1) {
    if (at > 0 && !isSpace(paragraph[at - 1])) return true
    // the closer may be the very next thing after the cut («**نکته**»)
    if (at === 0) return true
    at = paragraph.indexOf(delimiter, at + 1)
  }
  return false
}

/**
 * @param {string} visible the revealed prefix of `target`
 * @param {string} target  the text received so far
 * @param {{complete?: boolean}} [options] `complete`: `target` is the whole
 *   reply
 * @returns {string} what to hand to the Markdown renderer
 */
export function stabilizePartial(visible, target, options) {
  if (typeof visible !== 'string' || visible === '') return ''
  if (typeof target !== 'string' || visible.length >= target.length) {
    return visible
  }
  const open = openEmphasis(visible)
  if (!open.length) return visible

  const complete = !!(options && options.complete)
  const rest = target.slice(visible.length)
  const closers = []
  for (let i = open.length - 1; i >= 0; i -= 1) {
    // While text is still arriving the closer may simply not be here yet; in a
    // complete reply an opener without a closer is literal, as it will stay.
    if (complete && !closesLater(rest, open[i])) break
    closers.push(open[i])
  }
  if (!closers.length) return visible

  const body = visible.replace(/\s+$/, '')
  return body + closers.join('') + visible.slice(body.length)
}
